import {
  BoxGeometry,
  CylinderGeometry,
  MeshBasicMaterial,
  MeshPhongMaterial,
  Mesh,
  Group,
  EdgesGeometry,
  LineSegments,
  LineBasicMaterial,
  BackSide,
  AmbientLight,
  Vector3,
} from "three";

export const CUBE_PARAMS = {
  size: 120,
};

// 可視9辺の端点定義（ローカル座標、half=size/2）。
// rotation(0, PI/6)＋カメラz<0 の構成では +x=画面左, +y=上, -z=画面右, +z=画面奥
const H = CUBE_PARAMS.size / 2;
const CUBE_EDGES = {
  topLeft: [[H, H, -H], [H, H, H]], // 上面左の奥への辺（文献の辺A）
  topLeftBack: [[H, H, H], [H, H, 0]], // 半分
  topLeftFront: [[H, H, -H], [H, H, 0]], // 半分
  topRight: [[-H, H, -H], [-H, H, H]], // 上面右の奥への辺
  topRightBack: [[-H, H, H], [-H, H, 0]], // 半分
  topRightFront: [[-H, H, -H], [-H, H, 0]], //半分
  topFront: [[-H, H, -H], [H, H, -H]], // 上面手前（右側面上）の辺
  topBack: [[-H, H, H], [H, H, H]], // 上面奥の辺（文献の辺D）
  topBackLeft: [[3/4*H, H, H], [H, H, H]], // 上面奥の左端の辺（文献の辺D）
  topBackRight: [[-H, H, H], [-3/4*H, H, H]], // 上面奥の右端の辺（文献の辺D）
  bottomLeft: [[H, -H, -H], [H, -H, H]], // 左側面下の奥への辺
  bottomRight: [[-H, -H, -H], [-H, -H, H]], // 右側面下の奥への辺
  bottomFront: [[-H, -H, -H], [H, -H, -H]], // 右側面下の辺
  bottomBack: [[-H, -H, H], [H, -H, H]], // 左側面下の辺
  verticalBackRight: [[-H, -H, H], [-H, H, H]], // 奥の縦辺
  verticalBackRightTop: [[-H, 0, H], [-H, H, H]], // 半分
  verticalBackLeft: [[H, -H, H], [H, H, H]], // 奥の縦辺
  verticalBackLeftTop: [[H, 0, H], [H, H, H]], // 半分
  verticalFrontRight: [[-H, -H, -H], [-H, H, -H]], // 右奥の縦辺（文献の辺E相当）
  verticalFrontRightTop: [[-H, 0, -H], [-H, H, -H]], // 半分
  verticalFrontLeft: [[H, -H, -H], [H, H, -H]], // 左奥の縦辺（文献の辺E相当）
  verticalFrontLeftTop: [[H, 0, -H], [H, H, -H]], // 半分
};

// 学習者が選択する「辺の組み合わせ」→ 辺IDリスト。
export const CUBE_EDGE_GROUPS = {
  topandSide1: ["topLeftBack", "topRightBack", "topBack","verticalBackRightTop","verticalBackLeftTop"], // 上面の奥と左右の辺（文献の辺A+B+D+E）
  topandSide2: ["topLeft", "topRight", "topBack","verticalBackRight","verticalBackLeft"], // 上面の奥と右側の辺（文献の辺A+B+D+E）
  topandSide3: ["topLeftFront", "topRightFront", "topFront","verticalFrontRightTop", "verticalFrontLeftTop"], // 上面の手前と左右の辺
  topandSide4: ["topLeft", "topRight", "topFront","verticalFrontRight","verticalFrontLeft"], // 上面の手前と左右の辺
  topLeft: ["topLeft","verticalBackLeft","topBackLeft"], // 上面左の奥への辺
  topRight: ["topRight","verticalBackRight","topBackRight"], // 上面右の奥への辺
  bottomRight: ["bottomRight","verticalFrontRight","verticalBackRight","bottomFront","bottomBack"], // 右側面下の奥への辺
  bottomLeft: ["bottomLeft","verticalFrontLeft","verticalBackLeft","bottomFront","bottomBack"], // 左側面下の奥への辺
  topFront: ["topFront"], // 上面手前（右側面上）の辺
  topAll: ["topLeft","topRight","topFront","topBack"], // 上面の外周4辺（cube_model5: 上面が開く誤り）
  all: Object.keys(CUBE_EDGES),
};

// 辺定義の端点を「変形後のメッシュの辺の上」の点として求める。
// メッシュの辺は「変形後の立方体の角同士を結ぶ直線」なので、半分辺（端点が y=0 や z=0）を
// deformFn で直接変形してはいけない（deformFn は y>0/z>0 等の条件分岐を持ち、
// 角以外の点では実際の辺から外れてハイライトがずれる）。
// 代わりに、辺が乗っている軸を±Hまで延長した「角」を変形し、その間を線形補間する。
function deformedPointOnEdge(deformFn, p) {
  // p が乗っている full edge の軸（端点間で変化しうる軸）＝ |座標| < H の軸
  const axis = [0, 1, 2].find((i) => Math.abs(p[i]) < H);
  if (axis === undefined) {
    // p 自体が角
    return new Vector3(...deformFn(p[0], p[1], p[2]));
  }
  const cornerA = [...p];
  const cornerB = [...p];
  cornerA[axis] = -H;
  cornerB[axis] = H;
  const t = (p[axis] + H) / (2 * H);
  const da = deformFn(cornerA[0], cornerA[1], cornerA[2]);
  const db = deformFn(cornerB[0], cornerB[1], cornerB[2]);
  return new Vector3(
    da[0] + (db[0] - da[0]) * t,
    da[1] + (db[1] - da[1]) * t,
    da[2] + (db[2] - da[2]) * t
  );
}

// deformFn 適用後の2端点を結ぶ赤い円柱で辺を強調表示する
function createEdgeHighlight(deformFn, edgeId) {
  const [a, b] = CUBE_EDGES[edgeId];
  const start = deformedPointOnEdge(deformFn, a);
  const end = deformedPointOnEdge(deformFn, b);
  const dir = new Vector3().subVectors(end, start);
  const length = dir.length();
  const mesh = new Mesh(
    new CylinderGeometry(2, 2, length, 8),
    new MeshBasicMaterial({ color: 0xff0000 })
  );
  mesh.position.copy(start).addScaledVector(dir, 0.5);
  mesh.quaternion.setFromUnitVectors(new Vector3(0, 1, 0), dir.normalize());
  mesh.renderOrder = 3;
  return mesh;
}

// 奥行き比率: 手前(ローカル -z)=0 → 奥(ローカル +z)=1
// rotation.set(PI/6, PI/6, 0) とカメラ初期位置(z<0)の組ではローカル+zが画面奥にあたる
export function depthRatio(z) {
  const half = CUBE_PARAMS.size / 2;
  return (z + half) / CUBE_PARAMS.size;
}

// faceColors: { all, top, left, right } 真の面を赤くする（部品着色）
// BoxGeometryのグループ順は +x, -x, +y, -y, +z, -z。
// rotation(PI/6, PI/6)＋カメラz<0 の構成では +x=画面左の側面, +y=上面, -z=画面右の側面
function buildFaceMaterials(faceColors) {
  const RED = 0xff0000;
  // 皿・グラス（MeshPhongMaterial, 0xdddddd）と質感・色味を揃える。
  // MeshBasicMaterial だとライトを無視した真っ白になり、丸皿グラスと並べたとき色が浮く
  const colors = new Array(6).fill(0xdddddd);
  if (faceColors) {
    if (faceColors.all) colors.fill(RED);
    if (faceColors.left) colors[0] = RED;
    if (faceColors.top) colors[2] = RED;
    if (faceColors.right) colors[5] = RED;
  }
  return colors.map((c) => new MeshPhongMaterial({ color: c, shininess: 30 }));
}

// deformFn(x, y, z) => [x', y', z'] を BoxGeometry の各頂点に適用し、
// Cube.js（モチーフ）と同じ見た目（白面・黒アウトライン・黒エッジ・同回転・同ライト）で返す
// errorEdges: CUBE_EDGE_GROUPS のキー。指定した辺の組み合わせを赤く強調表示する
export function createDeformedCube(deformFn, faceColors, errorEdges) {
  const size = CUBE_PARAMS.size;
  const geometry = new BoxGeometry(size, size, size);
  const pos = geometry.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    const [x, y, z] = deformFn(pos.getX(i), pos.getY(i), pos.getZ(i));
    pos.setXYZ(i, x, y, z);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();

  const group = new Group();

  const outlineMat = new MeshBasicMaterial({ color: 0x000000, side: BackSide });
  const edgesMat = new LineBasicMaterial({ color: 0x000000 });

  const mesh = new Mesh(geometry, buildFaceMaterials(faceColors));

  const outline = new Mesh(geometry.clone(), outlineMat);
  outline.scale.multiplyScalar(1.02);

  // しきい値角度を上げる（既定1°）：変形で面が非平面になると四角形を三角形分割した
  // 対角線が「辺」として描かれてしまうため、緩い折れ（<25°）は辺とみなさない。
  // 立方体の実辺の二面角は変形後も約70〜110°なので消えない
  const edges = new LineSegments(new EdgesGeometry(geometry, 25), edgesMat);

  outline.renderOrder = 0;
  mesh.renderOrder = 1;
  edges.renderOrder = 2;

  group.add(outline, mesh, edges);

  const edgeIds = CUBE_EDGE_GROUPS[errorEdges];
  if (edgeIds) {
    edgeIds.forEach((edgeId) => group.add(createEdgeHighlight(deformFn, edgeId)));
  }

  group.rotation.set(0, 0, 0);

  // 皿・グラス（SPHERE_PLATE_PARAMS.light / GLASS_PARAMS.light）と同じ環境光にして
  // 立方体と丸皿グラスの見た目の明るさを揃える（主光源はシーン共通のDirectionalLight）
  const ambientLight = new AmbientLight(0x404040, 1.5);
  group.add(ambientLight);

  return group;
}
